import { LoadingIndicator } from '@droplet-tech-code/core-elements/module/ui/Loading';
import { Modal, useModal } from '@droplet-tech-code/core-elements/module/ui/Modal';
import { HStack, VStack } from '@droplet-tech-code/core-elements/module/ui/Stack';
import { Text } from '@droplet-tech-code/core-elements/module/ui/Text';
import { View } from '@droplet-tech-code/core-elements/module/ui/View';
import { isWeb } from '@droplet-tech-code/core-elements/module/utils/utils.helper';
import { CustomerClient } from '@naus-code/naus-admin-types';
import { useCallback } from 'react';

import { FlatList } from '~/components/FlatList/FlatList';
import { useCreateManualFormModal } from '~/components/Modals/ManualFormModal';
import { translate } from '~/utils/translation.utils';

import { useCustomerModal } from '../Customer.Modal';
import { customerApi } from '../utils/Customer.api';
import { CustomerDeletedPill } from '../utils/CusotmerInfo.utils';

const findCustomerByEmailModalKey = 'FindCustomerByEmail-ModalKey';

export const useFindCustomerByEmailInputModal = () => {
  const { open: openCustomerList } = useFindCustomerByEmailModal();

  return useCreateManualFormModal({
    title: translate('customer.findCustomerByEmail'),
    subtitle: translate('customer.enterEmailToFind'),
    formData: [
      {
        items: [
          {
            type: 'email',
            label: translate('customer.email'),
            id: 'email',
          },
        ],
      },
    ],
    onConfirm: ({ email }: { email: string }) => {
      openCustomerList(email);
    },
  });
};

export const useFindCustomerByEmailModal = () => {
  const { openModal, closeModal } = useModal();

  return {
    open: (email: string) => {
      openModal(<FindCustomerByEmailComponent email={email} />, {
        key: findCustomerByEmailModalKey,
        type: 'action-sheet-full',
      });
    },
    close: () => {
      closeModal(findCustomerByEmailModalKey);
    },
  };
};

const FindCustomerByEmailComponent = ({ email }: { email: string }) => {
  const { closeModal } = useModal();
  const { open: openCustomerModal } = useCustomerModal();
  const { data, isLoading } = customerApi.useFindCustomerListByEmailQuery({ email });

  const renderItem = useCallback(
    (customer: CustomerClient) => <CustomerListItem customer={customer} />,
    [email],
  );

  return (
    <Modal.Container w={isWeb ? 500 : undefined} h={isWeb ? 600 : undefined}>
      <Modal.Header
        title={translate('customer.findCustomerByEmail')}
        subtitle={email}
        divider
        onPress={() => {
          closeModal(findCustomerByEmailModalKey);
        }}
      />
      <Modal.Body flex={1}>
        {isLoading ? (
          <View p="5">
            <LoadingIndicator />
          </View>
        ) : (
          <FlatList
            feedback={translate('customer.noCustomersForEmail')}
            data={data?.customers ?? []}
            idKey="_id"
            renderItem={renderItem}
            onPressItem={(customer) => {
              openCustomerModal(customer._id, customer.p_email || email);
            }}
          />
        )}
      </Modal.Body>
    </Modal.Container>
  );
};

const CustomerListItem = ({ customer }: { customer: CustomerClient }) => {
  const { p_firstName, p_lastName, p_email, p_phone, _id } = customer;

  return (
    <VStack space="1" flex={1}>
      <HStack space="2.5" justify="space-between">
        <Text.Body2Regular>
          {`${p_firstName ?? ''} ${p_lastName ?? ''}`.trim()}
        </Text.Body2Regular>
        <CustomerDeletedPill customer={customer} />
      </HStack>
      <HStack space="2.5" justify="space-between">
        <Text.Small color="monochrome-extraDark">
          {translate('customer.emailUpper')}
        </Text.Small>
        <Text.Small align="right">{p_email || 'N/A'}</Text.Small>
      </HStack>
      <HStack space="2.5" justify="space-between">
        <Text.Small color="monochrome-extraDark">
          {translate('customer.phoneUpper')}
        </Text.Small>
        <Text.Small align="right">{p_phone || 'N/A'}</Text.Small>
      </HStack>
      <HStack space="2.5" justify="space-between">
        <Text.Small color="monochrome-extraDark">{translate('customer.id')}</Text.Small>
        <Text.Small align="right">{_id}</Text.Small>
      </HStack>
    </VStack>
  );
};
